// ** React Imports **
import { useNavigate } from "react-router-dom";
import { useState } from "react";

// ** React-Hook-Form import **
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// ** Firebase Imports **
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';

// ** Constant **
import { FormValidationMessages } from "src/constants/form.const";

// ** React-Redux **
import { useDispatch, useSelector } from "react-redux";
import { login } from "src/redux/actions/AuthAction";
import authConfig from "../../configs/auth";

// ** Snackbar for notifications **
import { useSnackbar } from 'notistack';

// ** Firebase Config **
import { authentication } from "src/configs/firebase-config";

// ** New UI Components **
import AuthHeader from '../../components/authentication/AuthHeader';
import AuthIntroSection from '../../components/authentication/AuthIntroSection';
import InputField from '../../components/authentication/InputField';
import LoadingOverlay from '../../components/authentication/LoadingOverlay';
import DeleteAccountCancelRequestDialog from '../account/DeleteAccountCancelRequestDialog';
import './login.css';

/**
 * LoginPage Component
 * 
 * Modern login page with social authentication and email/password login
 * Preserves all existing authentication logic from the original implementation
 * 
 * Features:
 * - Google OAuth login (existing logic preserved)
 * - Email/password login (existing logic preserved)
 * - Modern two-column layout
 * - Responsive design
 * 
 * @component
 */

// ** React-Hook-Form validation Schema (unchanged from original)
const validationSchema = yup.object().shape({
  email: yup
    .string()
    .email(FormValidationMessages.EMAIL.invalid)
    .required(FormValidationMessages.EMAIL.required),
  password: yup.string().required(FormValidationMessages.PASSWORD.required),
});

const LoginPage = () => {
  // ** Hooks (unchanged from original)
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loadingBtnLoader } = useSelector((state) => state.auth);

  // ** State Variables (unchanged from original)
  const [showPassword, setShowPassword] = useState(false);

  // ** React Hook Form setup (unchanged from original)
  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    watch,
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const formValues = watch();

  /**
   * Regular Email/Password Login (EXACT SAME LOGIC as original)
   * This function handles standard email/password authentication
   * It constructs the login payload and dispatches the login action
   * Upon successful login, user is redirected to dashboard
   */
  const onSubmit = async (data) => {
    try {
      // Construct the login payload for the API (unchanged)
      const payload = {
        userName: {
          2: data.email,  // Email login (type 2)
        },
        password: data.password,  // User's password
        device: 3,                // Device type (common value)
      };

      // Dispatch the login action to authenticate the user (unchanged)
      const completeLogin = await dispatch(login(payload));
      
      // If login is successful, redirect to home page (new UI)
      // Don't navigate if user account is deleted (dialog will show)
      if (completeLogin.type === "auth/login/fulfilled" && !completeLogin.payload?.isDeleted) {
        navigate("/home");
      }
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  /**
   * Google Login Implementation (EXACT SAME LOGIC as original)
   * This function handles Google OAuth authentication
   * It uses Firebase Authentication to get user credentials from Google
   * Then attempts to authenticate through the backend API
   */
  const signInWithGoogle = async () => {
    try {
      // Create a new Google Auth Provider instance (unchanged)
      const googleProvider = new GoogleAuthProvider();
      // Request email scope to get user's email address (unchanged)
      googleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
      
      // Open Google sign-in popup and wait for user to complete authentication (unchanged)
      const result = await signInWithPopup(authentication, googleProvider);
      
      // Construct payload for Google login API (unchanged)
      const payload = {
        userName: { 4: result.user.uid },    // Google UID (type 4 for OAuth)
        fullName: result.user.displayName,   // User's display name from Google
        device: 3,                           // Device type (common value)
        // Include email if available from Google profile
        ...(result?.user?.email && { userEmail: result.user.providerData[0].email }),
        // Include phone number if available from Google profile
        ...(result?.user?.phoneNumber && { userMobile: '+' + result.user.providerData[0].phoneNumber })
      };
      
      // Attempt to authenticate the user through the backend API (unchanged)
      const completeGoogleLogin = await dispatch(login(payload));
      
      if (completeGoogleLogin.type === "auth/login/fulfilled") {
        // Google authentication successful (unchanged)
        localStorage.setItem('type', 'user');
        
        // Generate a unique userId for local state management (unchanged)
        const userId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
        const userData = { userId: userId };
        localStorage.setItem('userData', JSON.stringify(userData));
        
        // Store the access token if provided by the backend (unchanged)
        if (completeGoogleLogin.payload?.accessToken) {
          localStorage.setItem(authConfig.storageTokenKeyName, completeGoogleLogin.payload.accessToken);
        }
        
        // Navigate to dashboard since user is now authenticated (unchanged)
        navigate("/home");
      }
      
    } catch (error) {
      console.error("Google login error:", error);
      
      // Show error message if Google authentication fails (unchanged)
      enqueueSnackbar('Google login failed. Please try again.', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 2000,
      });
    } 
  };

  /**
   * Apple Login Implementation
   * Uses Firebase Apple OAuth provider
   */
  const signInWithApple = async () => {
    try {
      const appleProvider = new OAuthProvider('apple.com');
      appleProvider.addScope('email');
      appleProvider.addScope('name');
      
      const result = await signInWithPopup(authentication, appleProvider);
      
      const payload = {
        userName: { 6: result.user.uid }, // Type 6 for Apple OAuth
        fullName: result.user.displayName || 'Apple User',
        device: 3,
        ...(result?.user?.email && { userEmail: result.user.providerData[0].email }),
        ...(result?.user?.phoneNumber && { userMobile: '+' + result.user.providerData[0].phoneNumber })
      };
      
      const completeAppleLogin = await dispatch(login(payload));
      
      if (completeAppleLogin.type === "auth/login/fulfilled") {
        localStorage.setItem('type', 'user');
        
        const userId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
        const userData = { userId: userId };
        localStorage.setItem('userData', JSON.stringify(userData));
        
        if (completeAppleLogin.payload?.accessToken) {
          localStorage.setItem(authConfig.storageTokenKeyName, completeAppleLogin.payload.accessToken);
        }
        
        navigate("/home");
      }
      
    } catch (error) {
      console.error("Apple login error:", error);
      
      enqueueSnackbar('Apple login failed. Please try again.', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 2000,
      });
    } 
  };

  // Password visibility toggle (unchanged from original)
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-page-new">
      {/* New Header Component */}
      <AuthHeader />

      {/* Main Content - Two Column Layout */}
      <div className="login-page-new__content">
        {/* Left Section - Branding & Illustration */}
        <AuthIntroSection
          heading="your digital growth command center"
          description="lorem ipsum is simply dummy text of the printing and typesetting industry. lorem ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled."
          image={null}
          imageAlt="welcome illustration"
        />

        {/* Right Section - Login Form Card */}
        <div className="login-page-new__card-wrapper">
          <div className="login-page-new__card" style={{ position: 'relative' }}>
            
            {/* Loading Overlay */}
            <LoadingOverlay isVisible={loadingBtnLoader} type="login" />
            {/* Card Title */}
            <h2 className="login-page-new__title">log in to your account</h2>

            {/* Social Login Buttons Section */}
            <div className="login-page-new__social">
              {/* Google Login Button - Uses existing signInWithGoogle logic */}
              <button
                type="button"
                className="login-page-new__social-btn"
                onClick={signInWithGoogle}
                disabled={loadingBtnLoader}
              >
                <span className="login-page-new__social-icon">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36362H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z" fill="#4285F4"/>
                    <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853"/>
                    <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54772 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                    <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
                  </svg>
                </span>
                <span className="login-page-new__social-text">continue with google</span>
              </button>

              {/* Apple Login Button */}
              <button
                type="button"
                className="login-page-new__social-btn"
                onClick={signInWithApple}
                disabled={loadingBtnLoader}
              >
                <span className="login-page-new__social-icon">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.4 9.6C14.4 7.2 16.2 6 16.2 6C15 4.2 13.2 4.2 12.6 4.2C11.1 4.1 9.6 5.1 8.9 5.1C8.1 5.1 6.9 4.2 5.7 4.2C3.6 4.3 1.2 6.3 1.2 10.2C1.2 11.7 1.5 13.2 2.1 14.7C2.7 16.2 4.8 19.8 6.6 19.8C7.8 19.8 8.4 19.2 9.9 19.2C11.4 19.2 11.7 19.8 13.2 19.8C15 19.8 16.8 16.5 17.4 15C17.4 15 14.4 13.5 14.4 9.6ZM12 2.7C13.2 1.2 12.9 0 12.9 0C11.7 0.1 10.5 0.9 9.9 1.8C9.3 2.7 9 3.6 9.1 4.5C10.2 4.6 11.1 3.9 12 2.7Z" fill="#000000"/>
                  </svg>
                </span>
                <span className="login-page-new__social-text">continue with apple</span>
              </button>
            </div>

            {/* Divider */}
            <div className="login-page-new__divider">
              <span className="login-page-new__divider-line"></span>
              <span className="login-page-new__divider-text">or</span>
              <span className="login-page-new__divider-line"></span>
            </div>

            {/* Manual Login Form - EXACT SAME LOGIC, NEW UI */}
            <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} className="login-page-new__form">
              
              {/* Email Input */}
              <InputField
                {...register("email")}
                type="email"
                placeholder="enter email address*"
                value={formValues.email}
                error={errors.email ? errors.email.message : ''}
                required
              />

              {/* Password Input with visibility toggle */}
              <div className="login-page-new__password-wrapper">
                <InputField
                  {...register("password")}
                  type={showPassword ? "text" : "password"}
                  placeholder="enter password*"
                  value={formValues.password}
                  error={errors.password ? errors.password.message : ''}
                  required
                />
                
                {/* Password visibility toggle */}
                <button
                  type="button"
                  className="login-page-new__password-toggle"
                  onClick={handleTogglePassword}
                  aria-label="toggle password visibility"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="login-page-new__submit-btn"
                disabled={loadingBtnLoader}
              >
                {loadingBtnLoader ? 'logging in...' : 'continue'}
              </button>
            </form>

            {/* Sign Up Link */}
            <div className="login-page-new__signup-link">
              <span className="login-page-new__signup-text">don't have an account? </span>
              <a href="/register" className="login-page-new__signup-link-text">
                sign up
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Cancel Request Dialog */}
      <DeleteAccountCancelRequestDialog />
    </div>
  );
};

export default LoginPage;
